import React, { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Spinner from '@dashboard/components/Spinner';
import { Alert, AlertDescription } from '@dashboard/components/ui/alert';
import EmptyState from '@dashboard/components/EmptyState';
import { submissionEmailLogsKey } from '@dashboard/lib/queryKeys';
import { DataGrid } from '@dashboard/components/DataGrid';
import { getEmailLogColumns } from '@dashboard/components/EmailLogColumns';
import EmailCustomerDialog from '@dashboard/components/EmailCustomerDialog';
import { buildEmailVariables } from '@dashboard/lib/emailVariables';
import { isBefore } from 'date-fns';
import { Button } from '@dashboard/components/ui/button';
import {
  ResendConfirmationButton,
  resendConfirmationOffReason,
} from '@dashboard/components/OrderPage';
import CardHeader from '@/dashboard/components/CardHeader';
import CardHeaderTitle from '@/dashboard/components/CardHeaderTitle';
import { confirm } from '@/dashboard/lib/confirm';
import { toast } from '@/dashboard/context/ToastContext';
import { ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const SubmissionsDetailEmailLogs = ({ submission }) => {
  const { sellerId, token } = useAuth();

  const {
    data: emailLogs,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: submissionEmailLogsKey(submission._id),
    queryFn: () => fetcher(`/sellers/${sellerId}/email-logs?submissionId=${submission._id}`, token),
    enabled: !!submission?._id,
  });

  const {
    mutate: sendSubmissionConfirmationEmail,
    isPending: isSendingSubmissionConfirmationEmail,
  } = useMutation({
    mutationFn: async () => {
      return await fetcher(
        `/sellers/${sellerId}/submissions/${submission._id}/send-confirmation-email`,
        token,
        {
          method: 'POST',
        },
      );
    },
    onSuccess: () => {
      refetch();
      toast({
        title: 'Confirmation email resent',
      });
    },
    onError: error => {
      refetch();
      toast({
        title: 'Failed to send confirmation email',
        description: error?.message,
        type: 'error',
      });
    },
  });

  async function handleSendSubmissionConfirmationEmail() {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendSubmissionConfirmationEmail();
    }
  }

  const {
    mutate: sendSubmissionNotificationEmail,
    isPending: isSendingSubmissionNotificationEmail,
  } = useMutation({
    mutationFn: async () => {
      return await fetcher(
        `/sellers/${sellerId}/submissions/${submission._id}/send-notification-email`,
        token,
        {
          method: 'POST',
        },
      );
    },
    onSuccess: () => {
      refetch();
      toast({
        title: 'Notification email resent',
      });
    },
    onError: error => {
      refetch();
      toast({
        title: 'Failed to send notification email',
        description: error?.message,
        type: 'error',
      });
    },
  });

  async function handleSendSubmissionNotificationEmail() {
    const confirmed = await confirm('Send a new notification email to you and your team?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendSubmissionNotificationEmail();
    }
  }

  const columns = useMemo(() => getEmailLogColumns(), []);
  const isLegacyUntrackedPeriod = isBefore(new Date(submission.createdAt), new Date('2024-12-17'));
  const emptyState = (
    <EmptyState>
      <EmptyState.Subtitle>
        {isLegacyUntrackedPeriod
          ? 'Email tracking unavailable before December 17, 2024'
          : 'No emails found for this submission.'}
      </EmptyState.Subtitle>
    </EmptyState>
  );

  return (
    <div className="flex flex-col lg:flex-row items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8 px-4 pt-2">
        <Card className="mx-auto !px-0 !py-2">
          <CardHeader className="mb-4 gap-2">
            <CardHeaderTitle>Emails</CardHeaderTitle>
            <div className="flex flex-wrap items-center gap-2">
              <ResendConfirmationButton
                offReason={resendConfirmationOffReason(submission, 'submission')}
                onClick={handleSendSubmissionConfirmationEmail}
                sending={isSendingSubmissionConfirmationEmail}
              />

              <Button
                variant="outline"
                size="sm"
                onClick={handleSendSubmissionNotificationEmail}
                disabled={isSendingSubmissionNotificationEmail}
              >
                Resend notification
                {isSendingSubmissionNotificationEmail ? <Spinner /> : <ArrowPathIcon />}
              </Button>
              <EmailCustomerDialog
                asButton
                customerEmail={submission.customer?.email}
                livemode={submission.livemode}
                objectLabel="submissions"
                endpoint={`/sellers/${sellerId}/submissions/${submission._id}/send-email`}
                variables={buildEmailVariables({ page: submission.page })}
                queryKey={submissionEmailLogsKey(submission._id)}
                emailsPath={`/submissions/${submission._id}/emails`}
              />
            </div>
          </CardHeader>

          <CardContent>
            {isError ? (
              <Alert variant="destructive" className="my-4">
                <ExclamationCircleIcon />
                <AlertDescription>Failed to load emails</AlertDescription>
              </Alert>
            ) : (
              <DataGrid
                columns={columns}
                data={emailLogs ?? []}
                isLoading={isLoading}
                emptyState={emptyState}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SubmissionsDetailEmailLogs;
